import * as React from 'react'
import _ from 'lodash'
import type { CommonDeps } from '../base/CommonDeps'
import { UnitImage } from './UnitImage'
import type { Renderable } from '../ui/SearchableDropdown'
import { type KnownUnit, type Unit, unitName, isKnownUnit } from './unit'
import type { TreeNode, Tree } from '../../../shared/lib/typeHelpers.no-deps'
import type { RenderCachedImage } from '../image/WithCachedImage'

export function getSearchTree(
    deps: CommonDeps,
    renderCachedImage: RenderCachedImage,
    units: Unit[],
    ancestors: KnownUnit[]
): Tree<Renderable<number>> {
    // Lookup for units
    const unitsLookup: _.Dictionary<Unit> = _.keyBy(units, 'id')
    // Lookup for ancestors
    const ancestorsLookup: _.Dictionary<KnownUnit> = _.keyBy(ancestors, 'id')
    // Filter out all the unique ancestors with no parent in ancestors as they will make the top level menu
    const parents = _.uniq(
        ancestors.filter((ancestor) => ancestor.parents.every((u) => ancestorsLookup[u] == null)).map((p) => p.id)
    )
    const tree = _.sortBy(
        getChildNodes(deps, renderCachedImage, [...parents], unitsLookup, ancestorsLookup),
        (treeNode) => treeNode.data.label.toLowerCase()
    )
    // These leave can appear on top level with parents
    const leavesWithNoParent: Array<TreeNode<Renderable<number>>> = units
        .filter(
            (unit) =>
                isKnownUnit(unit) && (!unit.parents.length || unit.parents.every((u) => ancestorsLookup[u] == null))
        )
        .map((leaf) => ({
            data: {
                id: leaf.id,
                label: unitName(leaf),
                leftNode: <UnitImage deps={deps} renderCachedImage={renderCachedImage} unit={leaf} />,
            },
            children: [],
        }))

    const searchTree = [...tree, ...leavesWithNoParent]
    return _.sortBy(searchTree, (treeNode) => treeNode.data.label.toLowerCase())
}

function getChildNodes(
    deps: CommonDeps,
    renderCachedImage: RenderCachedImage,
    children: number[],
    unitsLookup: _.Dictionary<Unit>,
    ancestorsLookup: _.Dictionary<KnownUnit>
): Array<TreeNode<Renderable<number>>> {
    if (!children.length) return []

    return children.map((child) => {
        const node = ancestorsLookup[child]
        // Check if child exist in ancestors as it can be sub node
        if (!node) {
            const leaf = unitsLookup[child]

            if (!leaf) return

            // Return a leaf
            return {
                data: {
                    id: leaf.id,
                    label: unitName(leaf),
                    leftNode: <UnitImage deps={deps} renderCachedImage={renderCachedImage} unit={leaf} />,
                },
                children: [],
            }
        } else {
            // Return a node
            return {
                data: {
                    id: node.id,
                    label: unitName(node),
                    leftNode: <UnitImage deps={deps} renderCachedImage={renderCachedImage} unit={node} />,
                },
                children: _.sortBy(
                    _.compact(getChildNodes(deps, renderCachedImage, node.children, unitsLookup, ancestorsLookup)),
                    (treeNode) => treeNode.data.label.toLowerCase()
                ),
            }
        }
    })
}
